import type { ReactNode } from 'react'
import { CalendarPlus, Check, Copy, Loader2, Pencil, Trash2, X } from 'lucide-react'
import {
  Button,
  DateField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@atrvd/saas-ui'
import { formatCurrency, formatDate } from '@dcp/shared'
import { AmountInput } from '../AmountInput'
import {
  amountPlaceholder,
  amountSignWarning,
  type AmountSignKind,
  type CategoryOption,
  type DraftErrors,
  type ProjectionCellFacility,
  type ProjectionDraft,
  type ProjectionRecord,
} from '@/lib/projection-cell'
import { rowControlId } from './manageProjectionsFocus'

/**
 * The data columns of the modal's table. Each row kind renders one cell per
 * column through the DataTable column `render`; the selection column is DataTable's.
 */
export type ProjectionColumn = 'date' | 'facility' | 'category' | 'description' | 'amount' | 'actions'

/**
 * The row's Selects sit in narrow cells of an auto-layout table: saas-ui clamps
 * the value by WORD (`line-clamp-1`), which showed only a name's first word, and
 * a bare nowrap would instead widen the column to the longest name. So the
 * trigger is capped at its column's width (the `w-40` / `w-52` headers minus
 * the cell padding) and the value truncates: "Golden Years Au…".
 */
const SELECT_TRIGGER_CLASS = 'h-9 [&>span]:min-w-0 [&>span]:truncate'
const FACILITY_TRIGGER_CLASS = `${SELECT_TRIGGER_CLASS} max-w-[8.5rem]`
const CATEGORY_TRIGGER_CLASS = `${SELECT_TRIGGER_CLASS} max-w-[11.5rem]`

interface RowIconButtonProps {
  id: string
  label: string
  onClick: () => void
  disabled?: boolean
  className?: string
  children: ReactNode
}

function RowIconButton({ id, label, onClick, disabled, className, children }: RowIconButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="ghost"
          size="icon"
          aria-label={label}
          onClick={onClick}
          disabled={disabled}
          className={cn('h-7 w-7 shrink-0', className)}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}

interface EditableTextProps {
  onEdit: () => void
  disabled: boolean
  className?: string
  children: ReactNode
}

function EditableText({ onEdit, disabled, className, children }: EditableTextProps) {
  return (
    <button
      type="button"
      onClick={onEdit}
      disabled={disabled}
      className={cn('rounded-sm text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', className)}
    >
      {children}
    </button>
  )
}

interface ProjectionViewCellProps {
  column: ProjectionColumn
  record: ProjectionRecord
  /** Name of the row's facility; shown only in the `facility` column. */
  facilityLabel: string
  /** Report label of the row's category; shown only in the `category` column. */
  categoryLabel: string
  busy: boolean
  onEdit: () => void
  onMoveToNextDay: () => void
  onCopy: () => void
  onDelete: () => void
}

/** One cell of a saved projection in view mode. Clicking its date, description or amount edits it. */
export function ProjectionViewCell({
  column,
  record,
  facilityLabel,
  categoryLabel,
  busy,
  onEdit,
  onMoveToNextDay,
  onCopy,
  onDelete,
}: ProjectionViewCellProps) {
  switch (column) {
    case 'date':
      return (
        <div className="flex items-center gap-1">
          <EditableText onEdit={onEdit} disabled={busy} className="tabular-nums">
            {formatDate(record.date)}
          </EditableText>
          <RowIconButton id={rowControlId(record.id, 'next-day')} label="Move to next day" onClick={onMoveToNextDay} disabled={busy}>
            <CalendarPlus className="h-4 w-4" aria-hidden="true" />
          </RowIconButton>
        </div>
      )
    case 'facility':
      return facilityLabel
    case 'category':
      return categoryLabel
    case 'description':
      return (
        <EditableText onEdit={onEdit} disabled={busy}>
          {record.description || <span className="italic text-muted-foreground">No description</span>}
        </EditableText>
      )
    case 'amount':
      return (
        <EditableText onEdit={onEdit} disabled={busy} className={cn('tabular-nums', record.amount < 0 && 'text-destructive')}>
          {formatCurrency(record.amount, { negativeStyle: 'parentheses' })}
        </EditableText>
      )
    case 'actions':
      return (
        <div className="flex items-center gap-0.5">
          <RowIconButton id={rowControlId(record.id, 'edit')} label="Edit" onClick={onEdit} disabled={busy}>
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </RowIconButton>
          <RowIconButton id={rowControlId(record.id, 'copy')} label="Copy" onClick={onCopy} disabled={busy}>
            <Copy className="h-4 w-4" aria-hidden="true" />
          </RowIconButton>
          <RowIconButton
            id={rowControlId(record.id, 'delete')}
            label="Delete"
            onClick={onDelete}
            disabled={busy}
            className="hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </RowIconButton>
        </div>
      )
    default: {
      const unreachable: never = column
      return unreachable
    }
  }
}

interface ProjectionEditCellProps {
  column: ProjectionColumn
  /** Projection id or new-row key; also the prefix of the field ids. */
  rowKey: string
  draft: ProjectionDraft
  errors: DraftErrors | undefined
  saving: boolean
  /** Choices of the `facility` column. */
  facilityOptions: readonly ProjectionCellFacility[]
  /** Choices of the `category` column. */
  categoryOptions: CategoryOption[]
  signKind: AmountSignKind
  onChange: (patch: Partial<ProjectionDraft>) => void
  onApply: () => void
  onDiscard: () => void
}

function describedBy(...ids: Array<string | false | undefined>): string | undefined {
  const present = ids.filter((id): id is string => typeof id === 'string')
  return present.length > 0 ? present.join(' ') : undefined
}

interface FieldErrorProps {
  id: string
  message: string | undefined
}

function FieldError({ id, message }: FieldErrorProps) {
  if (!message) return null
  return (
    <p id={id} className="mt-1 text-xs text-destructive">
      {message}
    </p>
  )
}

/** One cell of a projection (existing or new) in edit mode; validates on Apply, the sign warning never blocks. */
export function ProjectionEditCell({
  column,
  rowKey,
  draft,
  errors,
  saving,
  facilityOptions,
  categoryOptions,
  signKind,
  onChange,
  onApply,
  onDiscard,
}: ProjectionEditCellProps) {
  const errorId = (field: string) => `${rowKey}-${field}-error`

  switch (column) {
    case 'date':
      return (
        <>
          <DateField
            id={rowControlId(rowKey, 'date')}
            value={draft.date}
            onValueChange={(date) => onChange({ date })}
            aria-label="Projected payment date"
            aria-invalid={errors?.date ? true : undefined}
            aria-describedby={describedBy(errors?.date && errorId('date'))}
            className={cn('h-9', errors?.date && 'border-destructive')}
            disabled={saving}
          />
          <FieldError id={errorId('date')} message={errors?.date} />
        </>
      )
    case 'facility':
      return (
        <>
          <Select
            value={draft.facilityId}
            onValueChange={(facilityId) => {
              // Same Radix '' echo as the category Select below; only real ids pass.
              if (!facilityOptions.some((option) => option.id === facilityId)) return
              onChange({ facilityId })
            }}
            disabled={saving}
          >
            <SelectTrigger
              id={`${rowKey}-facility`}
              aria-label="Facility"
              aria-invalid={errors?.facilityId ? true : undefined}
              aria-describedby={describedBy(errors?.facilityId && errorId('facilityId'))}
              className={cn(FACILITY_TRIGGER_CLASS, errors?.facilityId && 'border-destructive')}
            >
              <SelectValue placeholder="Select facility" />
            </SelectTrigger>
            {/* Escape closes the list only, not the modal behind it. */}
            <SelectContent onEscapeKeyDown={(event) => event.stopPropagation()}>
              {facilityOptions.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError id={errorId('facilityId')} message={errors?.facilityId} />
        </>
      )
    case 'category':
      return (
        <>
          <Select
            value={draft.categoryId}
            onValueChange={(categoryId) => {
              // Radix echoes a spurious '' when the value is not among the
              // registered options (radix-ui/primitives#2817); only real ids pass.
              if (!categoryOptions.some((option) => option.id === categoryId)) return
              onChange({ categoryId })
            }}
            disabled={saving}
          >
            <SelectTrigger
              id={`${rowKey}-category`}
              aria-label="Category"
              aria-invalid={errors?.categoryId ? true : undefined}
              aria-describedby={describedBy(errors?.categoryId && errorId('categoryId'))}
              className={cn(CATEGORY_TRIGGER_CLASS, errors?.categoryId && 'border-destructive')}
            >
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            {/* Escape closes the list only, not the modal behind it. */}
            <SelectContent onEscapeKeyDown={(event) => event.stopPropagation()}>
              {categoryOptions.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldError id={errorId('categoryId')} message={errors?.categoryId} />
        </>
      )
    case 'description':
      return (
        <>
          <Input
            id={`${rowKey}-description`}
            value={draft.description}
            onChange={(event) => onChange({ description: event.target.value })}
            placeholder="Description"
            aria-label="Description"
            aria-invalid={errors?.description ? true : undefined}
            aria-describedby={describedBy(errors?.description && errorId('description'))}
            className={cn('h-9', errors?.description && 'border-destructive')}
            disabled={saving}
          />
          <FieldError id={errorId('description')} message={errors?.description} />
        </>
      )
    case 'amount': {
      const hintId = `${rowKey}-amount-hint`
      const warningId = `${rowKey}-amount-warning`
      const warning = amountSignWarning(signKind, draft.amount)
      return (
        <div>
          <div className="relative">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground"
            >
              $
            </span>
            <AmountInput
              id={`${rowKey}-amount`}
              value={draft.amount}
              onChange={(amount) => onChange({ amount })}
              placeholder={amountPlaceholder(signKind)}
              aria-label="Amount"
              aria-invalid={errors?.amount ? true : undefined}
              aria-describedby={describedBy(
                errors?.amount && errorId('amount'),
                signKind === 'outflow' && hintId,
                warning !== null && warningId,
              )}
              className={cn('h-9 pl-6 tabular-nums', errors?.amount && 'border-destructive')}
              disabled={saving}
            />
          </div>
          <FieldError id={errorId('amount')} message={errors?.amount} />
          {signKind === 'outflow' && (
            <p id={hintId} className="mt-1 text-xs text-muted-foreground">
              Outflow — enter a negative amount
            </p>
          )}
          {warning && (
            <p id={warningId} className="mt-1 text-xs text-amber-700">
              {warning}
            </p>
          )}
        </div>
      )
    }
    case 'actions':
      return (
        <div className="flex items-center gap-0.5">
          <RowIconButton id={rowControlId(rowKey, 'apply')} label="Apply" onClick={onApply} disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Check className="h-4 w-4" aria-hidden="true" />
            )}
          </RowIconButton>
          <RowIconButton id={rowControlId(rowKey, 'discard')} label="Discard" onClick={onDiscard} disabled={saving}>
            <X className="h-4 w-4" aria-hidden="true" />
          </RowIconButton>
        </div>
      )
    default: {
      const unreachable: never = column
      return unreachable
    }
  }
}
